import { useCallback, useEffect, useState } from "react";
import ReactFlow, {
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  useReactFlow,
  useUpdateNodeInternals,
} from "reactflow";
import "reactflow/dist/style.css";
import { Spin } from "antd";
import { initialNodes } from "./Config/initialNodes";
import { initialEdges } from "./Config/initialEdges";
import { nodeTypes } from "./Config/nodeTypes";
import { COLORS } from "./Styles/nodeStyles";

const PROGRESS_TO_NODE_ID = {
  GOVERNEYE_SETUP: "governeye-setup",
  BUSINESS_GOALS: "business-goals",
  DG_ASSESSMENT: "dg-assessment",
  DATA_STRATEGY: "data-strategy",
  USE_CASE_BOOK: "use-case-book",
  UC_PRIORITIZATION: "uc-prioritized",
  DATA_CATALOG: "data-catalog",
  DATA_QUALITY: "data-quality",
  SKEYE_SETUP: "skeye-setup",
  EDA: "eda",
  IMPLEMENTATION: "implementation",
  DASHBOARDING: "dashboarding",
  PREDICTIVE_ANALYTICS: "predictive-analytics",
};

const SKEYE_NODE_IDS = new Set([
  "skeye-setup",
  "eda",
  "implementation",
  "dashboarding",
  "predictive-analytics",
]);

const getGlowStyle = (baseStyle, isSkeye = false) => ({
  ...baseStyle,
  animation: isSkeye
    ? "nodeGlowBrown 1.5s ease-in-out infinite"
    : "nodeGlowOrange 1.5s ease-in-out infinite",
  boxShadow: isSkeye
    ? "0 0 12px 4px rgba(13, 46, 77, 1)"
    : "0 0 12px 4px rgba(217, 122, 7, 1)",
  zIndex: 10,
});

const FlowContent = ({ visible, projectData }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [isGraphReady, setIsGraphReady] = useState(false);

  const { fitView } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();

  // Apply glow to active progress nodes
  useEffect(() => {
    const progressArray = projectData?.progress || [];
    const activeNodeIds = progressArray
      .filter((p) => p !== "NOT_STARTED")
      .map((p) => PROGRESS_TO_NODE_ID[p])
      .filter(Boolean);

    setNodes(
      initialNodes.map((node) => {
        if (activeNodeIds.includes(node.id)) {
          const isSkeye = SKEYE_NODE_IDS.has(node.id);
          return {
            ...node,
            style: getGlowStyle(node.style || {}, isSkeye),
            data: node.data
              ? {
                  ...node.data,
                  style: node.data.style
                    ? getGlowStyle(node.data.style, isSkeye)
                    : node.data.style,
                }
              : node.data,
          };
        }
        return node;
      }),
    );
  }, [projectData, setNodes]);

  useEffect(() => {
    if (!visible) {
      setIsGraphReady(false);
      return;
    }

    const timer = setTimeout(() => {
      updateNodeInternals(initialNodes.map((n) => n.id));
      fitView({ padding: 0.15 });
      setIsGraphReady(true);
    }, 300);

    return () => clearTimeout(timer);
  }, [visible, fitView, updateNodeInternals]);

  const onConnect = useCallback(
    (params) =>
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            style: { stroke: COLORS.ORANGE, strokeWidth: 2 },
          },
          eds,
        ),
      ),
    [setEdges],
  );

  return (
    <div
      style={{
        height: "650px",
        border: "1px solid #dee2e6",
        position: "relative",
      }}
    >
      {/* Glow keyframe injected once */}
      <style>{`
        @keyframes nodeGlowOrange {
          0%   { box-shadow: 0 0 6px 2px rgba(217, 122, 7, 0.4); }
          50%  { box-shadow: 0 0 20px 8px rgba(217, 122, 7, 0.9); }
          100% { box-shadow: 0 0 6px 2px rgba(217, 122, 7, 0.4); }
        }
        @keyframes nodeGlowBrown {
          0%   { box-shadow: 0 0 6px 2px rgba(13, 46, 77, 0.4); }
          50%  { box-shadow: 0 0 20px 8px rgba(13, 46, 77, 0.9); }
          100% { box-shadow: 0 0 6px 2px rgba(13, 46, 77, 0.4); }
        }
      `}</style>

      {!isGraphReady && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#ffffff",
            zIndex: 10,
          }}
        >
          <Spin size="large" />
        </div>
      )}

      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        fitView={false}
        fitViewOptions={{ padding: 0.15, minZoom: 0.6, maxZoom: 1 }}
        defaultViewport={{ x: 0, y: 0, zoom: 0.7 }}
        style={{
          backgroundColor: "#ffffff",
          opacity: isGraphReady ? 1 : 0,
          transition: "opacity 0.3s ease-in-out",
        }}
        nodesDraggable={true}
        nodesConnectable={false}
        elementsSelectable={true}
        proOptions={{ hideAttribution: true }}
      >
        <Controls />
        <Background color="#dee2e6" gap={16} />
      </ReactFlow>
    </div>
  );
};

export default FlowContent;
